import { Component, EventEmitter, OnInit, Output, inject } from '@angular/core';
import { CommonModule, KeyValuePipe } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faBuilding,
  faBriefcase,
  faCircleCheck,
  faCircleExclamation,
  faXmark,
  faPlus,
  faHandshake,
  faTag,
  faCircleInfo,
  faLayerGroup,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import { PipelinesService } from '../../../../core/services/pipelines.service';
import { PartnerService } from '../../../../core/services/partner.service';
import { ProductsService } from '../../../../core/services/products.service';
import { Product, GroupedProducts } from '../../../../core/models/product.model';
import { Partner } from '../../../../core/models/partner.model';
import { Auth } from '../../../../core/auth/auth';
import { PopupInfoService } from '../../../../shared/services/popup-info.service';

@Component({
  selector: 'app-create-opportunity-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FontAwesomeModule, KeyValuePipe],
  templateUrl: './create-opportunity-modal.html',
  styleUrls: ['./create-opportunity-modal.scss'],
})
export class CreateOpportunityModal implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly pipelinesService = inject(PipelinesService);
  private readonly partnerService = inject(PartnerService);
  private readonly productsService = inject(ProductsService);
  private readonly authService = inject(Auth);
  private readonly popupInfoService = inject(PopupInfoService);

  @Output() close = new EventEmitter<void>();
  @Output() success = new EventEmitter<void>();

  // Icons
  faBuilding = faBuilding;
  faBriefcase = faBriefcase;
  faCircleCheck = faCircleCheck;
  faCircleExclamation = faCircleExclamation;
  faXmark = faXmark;
  faPlus = faPlus;
  faHandshake = faHandshake;
  faTag = faTag;
  faCircleInfo = faCircleInfo;
  faLayerGroup = faLayerGroup;
  faChevronRight = faChevronRight;

  opportunityForm: FormGroup;
  isLoading = false;
  isNifChecking = false;
  companyFound = false;
  foundCompanyName = '';
  foundHubspotCompanyId = '';

  budgetRanges = [
    { label: 'Hasta 2.000€', value: 2000 },
    { label: 'Entre 2.001€ y 20.000€', value: 10000 },
    { label: 'Por encima de 20.000€', value: 30000 },
  ];

  products: Product[] = [];
  groupedProducts: GroupedProducts[] = [];
  partners: Partner[] = [];
  selectedProductIds: number[] = [];
  expandedGroupIds = new Set<number | null>();
  tooltipPositions: { [key: number]: 'left' | 'center' | 'right' } = {};

  constructor() {
    this.opportunityForm = this.fb.group({
      // Empresa / Cliente
      nif: ['', [Validators.required, Validators.pattern(/^[0-9A-Z]{8,9}$/i)]],
      name: [{ value: '', disabled: true }, Validators.required],
      commercial_name: [{ value: '', disabled: true }],
      address: [{ value: '', disabled: true }],
      email: [{ value: '', disabled: true }, [Validators.email]],
      phone: [{ value: '', disabled: true }],

      // Negocio
      deal_name: ['', Validators.required],
      amount: [null, Validators.required],
      description: [''],

      // Admin only
      partner_id: [''],
    });
  }

  ngOnInit(): void {
    this.loadProducts();
    if (this.isAdmin) {
      this.loadPartners();
      this.opportunityForm.get('partner_id')?.setValidators(Validators.required);
    }
  }

  get f() {
    return this.opportunityForm.controls;
  }

  get isAdmin(): boolean {
    const user = this.authService.currentUser;
    return (user?.roles.includes('admin') && !user.partner_id) || false;
  }

  loadProducts(): void {
    this.productsService.listGrouped().subscribe({
      next: (grouped: GroupedProducts[]) => {
        this.groupedProducts = grouped;
        // Aplanamos para tener la lista de productos para el preview y cálculos
        this.products = grouped.flatMap((group) => group.products);
      },
      error: (err) => console.error('Error loading products:', err),
    });
  }

  loadPartners(): void {
    this.partnerService.getPartners().subscribe({
      next: (res) => (this.partners = res.data),
      error: (err) => console.error('Error loading partners:', err),
    });
  }

  checkNif(): void {
    const nif = this.opportunityForm.get('nif')?.value;
    if (!nif || nif.length < 8) return;

    this.isNifChecking = true;
    this.pipelinesService.checkCompanyByNif(nif).subscribe({
      next: (res) => {
        this.isNifChecking = false;
        if (res.success && res.data && res.data.exists && res.data.company) {
          this.companyFound = true;
          this.foundCompanyName = res.data.company.name;
          this.foundHubspotCompanyId = res.data.company.id;

          // Bloquear todos los campos de empresa ya que existe
          const fieldsToDisable = ['name', 'commercial_name', 'address', 'email', 'phone'];
          fieldsToDisable.forEach((field) => {
            const control = this.opportunityForm.get(field);
            control?.disable();
            control?.setValue('');
          });

          // También bloquear el NIF
          this.opportunityForm.get('nif')?.disable();
        } else {
          this.resetCompanyFields(true);
        }
      },
      error: () => {
        this.isNifChecking = false;
        this.resetCompanyFields(true);
      },
    });
  }

  clearCompany(): void {
    this.opportunityForm.get('nif')?.enable();
    this.opportunityForm.get('nif')?.setValue('');
    this.resetCompanyFields(false); // Vuelve a bloquear el resto hasta que se ponga NIF
  }

  private resetCompanyFields(enable: boolean): void {
    this.companyFound = false;
    this.foundCompanyName = '';
    this.foundHubspotCompanyId = '';

    const fields = ['name', 'commercial_name', 'address', 'email', 'phone'];
    fields.forEach((field) => {
      const control = this.opportunityForm.get(field);
      if (enable) {
        control?.enable();
      } else {
        control?.disable();
        control?.setValue('');
      }
    });
  }

  toggleProduct(productId: number): void {
    const index = this.selectedProductIds.indexOf(productId);
    if (index > -1) {
      this.selectedProductIds.splice(index, 1);
    } else {
      this.selectedProductIds.push(productId);
    }
  }

  isProductSelected(productId: number): boolean {
    return this.selectedProductIds.includes(productId);
  }

  toggleGroup(group: GroupedProducts): void {
    const groupProductIds = group.products.map((p) => p.id);
    if (this.isGroupSelected(group)) {
      this.selectedProductIds = this.selectedProductIds.filter(
        (id) => !groupProductIds.includes(id),
      );
    } else {
      const newIds = groupProductIds.filter((id) => !this.selectedProductIds.includes(id));
      this.selectedProductIds.push(...newIds);
    }
  }

  isGroupSelected(group: GroupedProducts): boolean {
    return group.products.length > 0 && group.products.every((p) => this.isProductSelected(p.id));
  }

  toggleGroupExpand(group: GroupedProducts, event: Event): void {
    event.stopPropagation();
    if (this.expandedGroupIds.has(group.id)) {
      this.expandedGroupIds.delete(group.id);
    } else {
      this.expandedGroupIds.add(group.id);
    }
  }

  isGroupExpanded(group: GroupedProducts): boolean {
    return this.expandedGroupIds.has(group.id);
  }

  get productsPreview(): string {
    if (this.selectedProductIds.length === 0) return '';
    const info = this.products
      .filter((p) => this.selectedProductIds.includes(p.id))
      .map((p) => `${p.name} (Comisión: ${p.commission}% - ${p.estimated_value})`)
      .join(', ');
    return `Productos interesados: ${info}`;
  }

  updateTooltipPosition(event: MouseEvent, productId: number): void {
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const modalBody = target.closest('.modal-body') as HTMLElement;

    if (!modalBody) return;

    const bodyRect = modalBody.getBoundingClientRect();
    const leftSpace = rect.left - bodyRect.left;
    const rightSpace = bodyRect.right - rect.right;

    // Si hay menos de 140px a la izquierda, alinear a la izquierda
    if (leftSpace < 140) {
      this.tooltipPositions[productId] = 'left';
    }
    // Si hay menos de 140px a la derecha, alinear a la derecha
    else if (rightSpace < 140) {
      this.tooltipPositions[productId] = 'right';
    }
    // En el resto de casos, centrar
    else {
      this.tooltipPositions[productId] = 'center';
    }
  }

  onSubmit(): void {
    if (this.opportunityForm.invalid) {
      this.opportunityForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;

    // Obtener valores (incluyendo los deshabilitados)
    const rawValues = this.opportunityForm.getRawValue();

    // Asegurar MAYÚSCULAS en NIF y Nombre
    const nif = (rawValues.nif || '').toUpperCase();
    const name = (rawValues.name || '').toUpperCase();

    // Preparar descripción con productos
    let finalDescription = rawValues.description || '';
    if (this.selectedProductIds.length > 0) {
      const selectedProductsInfo = this.products
        .filter((p) => this.selectedProductIds.includes(p.id))
        .map((p) => `${p.name} (Comisión: ${p.commission}% - ${p.estimated_value})`)
        .join(', ');

      const productsText = `\n\nProductos interesados: ${selectedProductsInfo}`;
      finalDescription += productsText;
    }

    // Estructura de payload simplificada si existe empresa
    const payload: any = {
      dealname: rawValues.deal_name,
      amount: rawValues.amount,
      description: finalDescription,
      nif: nif,
      partner_id: rawValues.partner_id,
    };

    if (this.companyFound) {
      payload.hubspot_company_id = this.foundHubspotCompanyId;
      payload.company_exists = true;
    } else {
      payload.name = name;
      payload.commercial_name = rawValues.commercial_name;
      payload.address = rawValues.address;
      payload.email = rawValues.email;
      payload.phone = rawValues.phone;
      payload.company_exists = false;
    }

    this.pipelinesService.createOpportunity(payload).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res.success) {
          this.popupInfoService.showSuccess('Oportunidad creada correctamente');
          this.success.emit();
        } else {
          this.popupInfoService.showError(res.message || 'Error al crear la oportunidad');
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.popupInfoService.showError('Error de servidor al crear la oportunidad');
        console.error('Error creating opportunity:', err);
      },
    });
  }

  onClose(): void {
    this.close.emit();
  }
}
